import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Common } from '../utils/common';

/**
 * @typedef {Object} CallRescoreData
 * @property {CallType[]} call_types
 * @property {ScoringSettings | null} scoring_settings
 *
 * @typedef {Object} CallType
 * @property {number} call_type_id
 * @property {string} call_type_text
 * @property {number} call_status_id
 * @property {string} call_status_text
 * @property {string} processing_queue_name
 * @property {number} processing_queue_type_id
 * @property {null | 1} dealsaver_enabled
 *
 * @typedef {Object} ScoringSettings
 * @property {string} location_name
 * @property {string} audiourl
 * @property {string} call_start_time
 * @property {string} duration
 * @property {string} ani
 * @property {string} domain_name
 * @property {string} call_type_id
 * @property {string} call_status_id
 * @property {string | null} call_summary
 * @property {string | null} call_alert_text
 * @property {string | null} caller_name
 * @property {string | null} caller_email
 * @property {string | null} caller_alt_phone
 * @property {string | null} initial_contact
 * @property {string} timezone_offset
 * @property {string | null} department
 * @property {string | null} department_id
 * @property {string | null} appointment_set
 * @property {string | null} appointment_offered
 * @property {string | null} appointment_date
 * @property {string | null} call_alert_id
 * @property {string | null} employee_name
 * @property {string | null} representative_user_id
 * @property {string} domain_id
 */

/**
 * @param {string} callId
 * @returns {Promise<CallRescoreData>}
 */
async function fetchRescoreData(callId) {
  const response = await fetch('/rescore', {
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ call_id: callId }),
  });

  if (response.status === 403) {
    throw new Error('You do not have permission to rescore this call');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch rescore data');
  }

  const data = await response.json();

  if (!data.scoring_settings) {
    throw new Error('Rescoring not available for this call');
  }

  // Convert all numbers to strings to use in form inputs
  Object.keys(data.scoring_settings).forEach((key) => {
    if (data.scoring_settings[key] !== null) {
      data.scoring_settings[key] = data.scoring_settings[key].toString();
    }
  });

  return data;
}

/**
 * @param {string} callId
 */
export function useRescoreData(callId) {
  return useQuery({
    queryKey: ['rescore', callId],
    queryFn: () => fetchRescoreData(callId),
    refetchOnWindowFocus: false,
    retry(failureCount, error) {
      if (
        error instanceof Error &&
        (error.message === 'You do not have permission to rescore this call' ||
          error.message === 'Rescoring not available for this call')
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * @typedef {{
 *   call_alerts: { id: number; text: string }[];
 *   departments: { id: number; text: string }[];
 * }} DealSaverInfo
 */

/**
 * @param {number | undefined} processingQueueId
 * @returns {Promise<DealSaverInfo>}
 */
async function fetchDealSaverInfo(processingQueueId) {
  if (!processingQueueId) return Promise.reject(new Error('No processing queue id provided'));

  const response = await fetch('/rescore/dealsaver', {
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ processing_queue_type_id: processingQueueId }),
  });

  if (!response.ok) {
    return Promise.reject(new Error('Failed to fetch dealsaver info'));
  }

  return response.json();
}

/**
 * @param {number | undefined} processingQueueId
 */
export function useDealSaverInfo(processingQueueId) {
  return useQuery({
    queryKey: ['rescore-dealsaver', processingQueueId],
    queryFn: () => fetchDealSaverInfo(processingQueueId),
    enabled: Boolean(processingQueueId),
    // This data is mostly static, so it should never be considered stale
    staleTime: Infinity,
  });
}

/**
 * @typedef {{ employee_name: string, employee_id: number }} SalesRep
 */

/**
 * @param {number} domainId
 * @returns {Promise<SalesRep[]>}
 */
async function fetchSalesReps(domainId) {
  const response = await fetch(`/reports/representatives/${domainId}`);
  if (!response.ok) {
    return Promise.reject(new Error('Failed to fetch dealsaver info'));
  }
  const data = await response.json();
  const formattedData = data.map((representative) => ({
    employee_name: `${representative.first_name} ${representative.last_name}`,
    employee_id: representative.user_id,
  }));

  return formattedData;
}

/**
 * @param {string} domainId
 */
export function useSalesReps(domainId) {
  return useQuery({
    queryKey: ['sales-reps', domainId],
    queryFn: () => fetchSalesReps(domainId),
    refetchOnWindowFocus: false,
  });
}

/**
 * @typedef {Pick<
 *   ScoringSettings,
 *   | 'call_type_id'
 *   | 'call_status_id'
 *   | 'appointment_set'
 *   | 'appointment_date'
 *   | 'representative_user_id'
 *   | 'call_alert_id'
 *   | 'caller_name'
 *   | 'initial_contact'
 *   | 'caller_alt_phone'
 *   | 'appointment_offered'
 *   | 'caller_email'
 *   | 'call_summary'
 *   | 'department_id'
 * > & { call_id: string }} UpdateScoreVariables
 */

/**
 * @param {UpdateScoreVariables} variables
 */
async function updateScores(variables) {
  const response = await fetch('/rescore/update_scores', {
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(variables),
  });

  if (response.status === 403) {
    return Promise.reject(new Error('You do not have permission to rescore this call'));
  }

  if (!response.ok) {
    return Promise.reject(new Error('Failed to rescore call'));
  }

  return response.json();
}

/**
 * @param {string} callId
 */
export function useUpdateScores(callId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateScores,
    onSuccess() {
      queryClient.invalidateQueries(['rescore', callId]);
      Common.notify('Call Rescored Successfully', 'success');
    },
    onError(error) {
      Common.notify(error instanceof Error ? error.message : 'Failed to rescore call', 'error');
    },
  });
}
